import { PropsWithChildren, useEffect, useState, useCallback } from "react";
import { useSearchParams, URLSearchParamsInit } from "react-router-dom";
import { useMutation, useQuery } from "@apollo/client";
import classNames from "classnames";
import { useFeatureFlagEnabled } from "posthog-js/react";
import { Image } from "@mantine/core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCopy } from "@fortawesome/sharp-regular-svg-icons";
import ProviderUserForm, {
  providerOrganizationType,
  ProviderUserFormValues,
} from "./ProviderUserForm";
import { gql } from "../../__generated__";
import { ContinueButton } from "../buttons/ContinueButton";
import Loader from "../shared/Loader";
import { PageError } from "../PageError";
import InlineBanner from "../shared/InlineBanner";
import ProviderBAAForm from "../ProviderBAAForm/ProviderBAAForm";
import { Button } from "../buttons/Button/Button";
import Tip from "../Tip";
import UserForm, { UserFormValues } from "./UserForm";
import { DownloadAgreementButton } from "../buttons/DownloadAgreementButton";
import { OrganizationType } from "@/__generated__/graphql";
import { getReferrableConditions } from "shared/src/data/medication-condition-pairs";

import styles from "./user-provider-setup-wrapper.module.scss";

export const CREATE_PROVIDER = gql(`
  mutation CreateProvider($input: CreateProviderViaSelfSignUpInput!){
    createProviderViaSelfSignUp(input: $input) {
      providerId
      hasSignedBAA
    }
  }
`);

export const UPDATE_PROVIDER_USER_INFO = gql(`
    mutation UpdateProviderUserInfo($input: UpdateProviderUserInfoInput!) {
      updateProviderUserInfo(input: $input) {
        providerUserId
      }
    }
`);

export const MARK_BAA_SIGNED = gql(`
    mutation SignBAA {
      signProviderBusinessAssociateAgreement {
        providerId
        hasSignedBAA
      }
    }
`);

export const GET_PROVIDER = gql(`
  query ProviderQuery {
    provider {
      providerId
      hasSignedBAA
      type
    }
  }
`);

export const GET_PROVIDER_USER = gql(`
  query ProviderUserQuery {
    providerUser {
      providerUserId
      firstName
      lastName
      jobTitle
    }
  }
`);

export const GET_BAA_DOWNLOAD_LINK = gql(`
  mutation DownloadBAAFormLink {
    downloadBAAFormLink
  }
`);

export function UserProviderSetupWrapper({ children }: PropsWithChildren) {
  const [, setSearchParams] = useSearchParams();
  const [accountSetup, setAccountSetup] = useState(false);
  const [isAuthorizedToSignBAA, setIsAuthorizedToSignBAA] = useState(true);
  const [isCreateMutationsLoading, setIsCreateMutationsLoading] =
    useState(false);
  const [providerBAASigned, setProviderBAASigned] = useState(false);
  const [hasErrorOnSubmit, setHasErrorOnSubmit] = useState(false);
  const [hasConfirmedEligibility, setHasConfirmedEligibility] = useState(false);
  const {
    data: providerUserData,
    loading: providerUserLoading,
    error: providerUserError,
  } = useQuery(GET_PROVIDER_USER);
  const {
    data: providerData,
    loading: providerLoading,
    error: providerError,
  } = useQuery(GET_PROVIDER);
  const [shouldPromptToDownloadBAA, setPromptToDownloadBAA] = useState(false);
  const isEnterpriseProvider =
    providerData?.provider?.type === "enterprise_provider";
  const [createProvider, { loading: createProviderLoading }] = useMutation(
    CREATE_PROVIDER,
    {
      onError: () => {
        setHasErrorOnSubmit(true);
      },
    },
  );

  const handleSearch = useCallback(
    (newParams: URLSearchParamsInit) => setSearchParams(newParams),
    [setSearchParams],
  );

  useEffect(() => {
    if (
      !providerUserLoading &&
      !providerLoading &&
      !providerUserData?.providerUser &&
      !providerData?.provider
    ) {
      setAccountSetup(true);
    }
  }, [providerUserData, providerData, providerUserLoading, providerLoading]);

  useEffect(() => {
    if (accountSetup) {
      // Step 1: Provider Form
      if (!providerData?.provider && !providerUserData?.providerUser) {
        handleSearch({ newUserForm: "ProviderForm" });
        return;
      }

      // Step 2: Confirmed Eligibility (skip for enterprise providers)
      if (
        !isEnterpriseProvider &&
        !hasConfirmedEligibility &&
        !providerData?.provider?.hasSignedBAA
      ) {
        handleSearch({ newUserForm: "ConfirmedEligibility" });
        return;
      }

      // Step 3: Sign BAA (skip for enterprise providers)
      if (
        !isEnterpriseProvider &&
        hasConfirmedEligibility &&
        !providerData?.provider?.hasSignedBAA
      ) {
        handleSearch({ newUserForm: "SignBAA" });
        return;
      }

      // Step 4: User Form
      if (
        !providerUserData?.providerUser?.firstName ||
        !providerUserData?.providerUser?.lastName ||
        !providerUserData?.providerUser?.jobTitle
      ) {
        handleSearch({ newUserForm: "UserForm" });
        return;
      }

      // Step 5: Download BAA (skip for enterprise providers)
      if (
        !isEnterpriseProvider &&
        providerData?.provider &&
        providerData?.provider?.hasSignedBAA &&
        shouldPromptToDownloadBAA
      ) {
        handleSearch({ newUserForm: "DownloadBAA" });

        return;
      }
      setSearchParams({});
    }
  }, [
    accountSetup,
    hasConfirmedEligibility,
    providerData?.provider?.hasSignedBAA,
    providerData?.provider?.type,
    providerUserData?.providerUser?.firstName,
    providerUserData?.providerUser?.lastName,
    providerUserData?.providerUser?.jobTitle,
    shouldPromptToDownloadBAA,
    handleSearch,
    isEnterpriseProvider,
  ]);

  const [signBAA, { loading: signBAALoading }] = useMutation(MARK_BAA_SIGNED, {
    onError: () => {
      setHasErrorOnSubmit(true);
    },
  });

  const [downloadBAA] = useMutation(GET_BAA_DOWNLOAD_LINK, {
    fetchPolicy: "network-only",
    onError: () => {
      setHasErrorOnSubmit(true);
    },
  });

  const [updateProviderUserInfo, { loading: createProviderUserLoading }] =
    useMutation(UPDATE_PROVIDER_USER_INFO, {
      onError: () => {
        setHasErrorOnSubmit(true);
      },
    });

  useEffect(() => {
    setIsCreateMutationsLoading(
      createProviderLoading || createProviderUserLoading,
    );
  }, [createProviderLoading, createProviderUserLoading]);

  const handleUserFormSubmit = async (formData: UserFormValues) => {
    await updateProviderUserInfo({
      variables: {
        input: {
          firstName: formData.firstName as string,
          lastName: formData.lastName as string,
          jobTitle: formData.jobTitle as string,
        },
      },
      refetchQueries: [{ query: GET_PROVIDER_USER }, { query: GET_PROVIDER }],
    });
  };

  const handleProviderUserFormSubmit = async (
    formData: ProviderUserFormValues,
  ) => {
    setIsCreateMutationsLoading(true);
    setHasErrorOnSubmit(false);

    const isProviderOrgType =
      formData.organizationType === providerOrganizationType;
    const identificationNumber = isProviderOrgType
      ? formData.physicianNPI
      : formData.advocacyGroupEIN;
    const organizationType = isProviderOrgType
      ? OrganizationType.Provider
      : OrganizationType.AdvocacyGroup;
    const { data: providerResponse } = await createProvider({
      variables: {
        input: {
          providerName: formData.organizationName as string,
          identificationNumber,
          organizationType,
          specialty: isProviderOrgType ? formData.specialty : null,
        },
      },
      refetchQueries: [{ query: GET_PROVIDER }],
    });

    if (!providerResponse) {
      setIsCreateMutationsLoading(false);

      throw new Error("Failed to create provider");
    }
    await handleUserFormSubmit({
      firstName: formData.firstName,
      lastName: formData.lastName,
      jobTitle: formData.jobTitle,
    });
  };

  const handleSignBAA = async () => {
    setHasErrorOnSubmit(false);
    setPromptToDownloadBAA(true);

    if (!providerData?.provider?.providerId) {
      setHasErrorOnSubmit(true);

      throw new Error(
        "No provider ID to sign BAA. This should not be possible.",
      );
    }
    await signBAA({
      refetchQueries: [{ query: GET_PROVIDER }],
    });
  };

  const handleDownloadBAA = async () => {
    setHasErrorOnSubmit(false);

    if (!providerData?.provider?.providerId) {
      setHasErrorOnSubmit(true);

      throw new Error(
        "No provider ID to sign BAA. This should not be possible.",
      );
    }

    const { data } = await downloadBAA({
      refetchQueries: [{ query: GET_PROVIDER }],
    });

    const link = data?.downloadBAAFormLink;

    if (!link) {
      throw new Error("Download link not returned by the server.");
    }

    window.open(link, "_blank");
  };

  if (providerLoading || providerUserLoading) {
    return (
      <div className={classNames(styles.loadingWrapper)}>
        <Loader color="loader_almost_black" height="48" width="48" />
      </div>
    );
  }

  if (providerError || providerUserError) {
    return <PageError />;
  }

  if (!providerUserData?.providerUser && !providerData?.provider) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.pageContainer}>
          {hasErrorOnSubmit && (
            <InlineBanner
              message={
                <span className={styles.errorText}>
                  {"We ran into an issue. Try again or "}
                  <a
                    className={styles.supportLink}
                    href="mailto:support@getclaimable.com"
                  >
                    {"Contact Support"}
                  </a>
                </span>
              }
              type="error"
              dismissable
              onDismiss={() => setHasErrorOnSubmit(false)}
              className={styles.errorMessage}
            />
          )}
          <h2 className={styles.subheading}>
            {"Almost there! Set up your account"}
          </h2>
          <h1 className={styles.heading}>
            {"Tell us some basic information about you"}
          </h1>
          <ProviderUserForm
            formId="providerInfoForm"
            onSubmit={handleProviderUserFormSubmit}
          />
          <ContinueButton
            type="submit"
            form="providerInfoForm"
            className={styles.continueButton}
            isLoading={isCreateMutationsLoading}
            disabled={isCreateMutationsLoading}
          />
        </div>
      </div>
    );
  }

  if (
    !isEnterpriseProvider &&
    providerData?.provider &&
    !hasConfirmedEligibility &&
    !providerData?.provider.hasSignedBAA
  ) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.eligibilityPageContainer}>
          <h2 className={styles.subheading}>
            {"It's great to have you on board!"}
          </h2>
          <h1 className={styles.heading}>
            {"Claimable currently supports medication denials for:"}
          </h1>
          <ul className={styles.conditionList}>
            {getReferrableConditions()
              .sort()
              .map((condition) => (
                <li key={condition}>{condition}</li>
              ))}
          </ul>
          <Tip
            variant="callout"
            className={styles.eligibilityTip}
            heading="We're working every day to support more denied claims"
            link={{
              href: "https://www.getclaimable.com/provider-waitlist",
              display: "Don't see what you're looking for?",
            }}
          >
            {
              "Don't see the condition that you need? You can continue to sign up, or click below and we'll notify you when it's available."
            }
          </Tip>
          <ContinueButton
            onClick={() => setHasConfirmedEligibility(true)}
            className={styles.continueButton}
          >
            {"Continue"}
          </ContinueButton>
        </div>
      </div>
    );
  }

  if (
    !isEnterpriseProvider &&
    providerData?.provider &&
    !providerData?.provider.hasSignedBAA
  ) {
    if (!isAuthorizedToSignBAA) {
      return (
        <div className={styles.pageWrapper}>
          <div className={styles.agreementPageContainer}>
            <h2 className={styles.subheading}>{"Unlock Claimable"}</h2>
            <h1 className={styles.heading}>
              {
                "Ask someone who is able to agree to create an account and accept the BAA."
              }
            </h1>
            <Tip heading="Who should this be">
              {
                "This is usually a practice owner, practice manager, or business admin but varies based on the size and structure of your organization."
              }
            </Tip>
            <button
              className={styles.copyButton}
              type="button"
              onClick={() =>
                navigator.clipboard.writeText(
                  "https://provider.getclaimable.com",
                )
              }
            >
              {"Copy signup link"}
              <FontAwesomeIcon icon={faCopy} />
            </button>
            <ContinueButton
              onClick={() => setIsAuthorizedToSignBAA(true)}
              className={styles.continueButton}
            >
              {"Done"}
            </ContinueButton>
            <a
              className={styles.helpLink}
              href="mailto:support@getclaimable.com"
            >
              {"Need help? Contact support"}
            </a>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.pageWrapper}>
        <div className={styles.agreementPageContainer}>
          {hasErrorOnSubmit && (
            <InlineBanner
              message={
                <span className={styles.errorText}>
                  {"We ran into an issue. Try again or "}
                  <a
                    className={styles.supportLink}
                    href="mailto:support@getclaimable.com"
                  >
                    {"Contact Support"}
                  </a>
                </span>
              }
              type="error"
              dismissable
              onDismiss={() => setHasErrorOnSubmit(false)}
              className={styles.errorMessage}
            />
          )}
          <h2 className={styles.subheading}>{"Let's make it official"}</h2>
          <h1 className={styles.heading}>{"Business Associate Agreement"}</h1>
          <ProviderBAAForm
            onCheckboxClicked={(event) =>
              setProviderBAASigned(event.target.checked)
            }
          />
          <ContinueButton
            disabled={!providerBAASigned || signBAALoading}
            onClick={handleSignBAA}
            isLoading={signBAALoading}
            className={styles.continueButton}
          >
            {"Agree and Continue"}
          </ContinueButton>
          <Button
            variant="tertiary"
            size="small"
            onClick={() => {
              setIsAuthorizedToSignBAA(false);
            }}
            className={styles.cantSignBAAButton}
          >
            {"I'm unable to agree on behalf of my organization"}
          </Button>
        </div>
      </div>
    );
  }

  if (
    !providerUserData?.providerUser?.firstName ||
    !providerUserData.providerUser.lastName ||
    !providerUserData.providerUser.jobTitle
  ) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.pageContainer}>
          {hasErrorOnSubmit && (
            <InlineBanner
              message={
                <span className={styles.errorText}>
                  {"We ran into an issue. Try again or "}
                  <a
                    className={styles.supportLink}
                    href="mailto://support@getclaimable.com"
                  >
                    {"Contact Support"}
                  </a>
                </span>
              }
              type="error"
              dismissable
              onDismiss={() => setHasErrorOnSubmit(false)}
              className={styles.errorMessage}
            />
          )}
          <h2 className={styles.subheading}>
            {"Almost there! Set up your account"}
          </h2>
          <h1 className={styles.heading}>
            {"Tell us some basic information about you"}
          </h1>
          <UserForm
            formId="providerUserInfoForm"
            onSubmit={handleUserFormSubmit}
          />
          <ContinueButton
            type="submit"
            form="providerUserInfoForm"
            className={styles.continueButton}
            isLoading={isCreateMutationsLoading}
            disabled={isCreateMutationsLoading}
          />
        </div>
      </div>
    );
  }

  if (
    !isEnterpriseProvider &&
    providerData?.provider &&
    providerData?.provider.hasSignedBAA &&
    shouldPromptToDownloadBAA
  ) {
    return (
      <div className={styles.pageWrapper}>
        <div className={styles.downloadPageContainer}>
          {hasErrorOnSubmit && (
            <InlineBanner
              message={
                <span className={styles.errorText}>
                  {"We ran into an issue. Try again or "}
                  <a
                    className={styles.supportLink}
                    href="mailto://support@getclaimable.com"
                  >
                    {"Contact Support"}
                  </a>
                </span>
              }
              type="error"
              dismissable
              onDismiss={() => setHasErrorOnSubmit(false)}
              className={styles.errorMessage}
            />
          )}
          <h2 className={styles.subheading}>{"Thats it!"}</h2>
          <h1 className={styles.heading}>
            {"You're ready to join the fight against unjust denials"}
          </h1>
          <div className={styles.downloadContainer}>
            <Image src="/images/BAAandServiceAgreementRecipt.svg" />

            <DownloadAgreementButton onClick={handleDownloadBAA}>
              {" Download agreement "}
            </DownloadAgreementButton>
          </div>
          <ContinueButton
            onClick={() => {
              setPromptToDownloadBAA(false);
              setAccountSetup(false);
            }}
            className={styles.continueButton}
          >
            {"Done"}
          </ContinueButton>
        </div>
      </div>
    );
  }

  return children;
}

export function UserProviderSetupWrapperWithFeatureFlag({
  children,
}: PropsWithChildren) {
  const featureEnabled = useFeatureFlagEnabled("self-serve-provider-signup");

  if (featureEnabled) {
    return <UserProviderSetupWrapper>{children}</UserProviderSetupWrapper>;
  }

  return children;
}
